import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Save, Loader2, Star } from 'lucide-react';
import ConfirmActionDialog from '@/components/ConfirmActionDialog';
import QueSeConfiguraAca from '@/components/QueSeConfiguraAca';

/**
 * Si al que pidió le sirvió.
 *
 * **No es un aviso más, y por eso está arriba de ellos y no adentro.** La encuesta no se dispara
 * sola: se pregunta *dentro* de un aviso, escribiendo `{{link_conformidad}}` en su texto. Esta
 * tarjeta define **qué se pregunta**; el catálogo de abajo define **cuándo**. Separarlo es lo que
 * permite preguntar al cerrar, o al entregar, o en las dos, sin tocar código.
 *
 * **Prenderla le manda una pregunta a gente real**, así que se confirma igual que un aviso.
 * Apagarla no: dejar de preguntar nunca necesitó permiso.
 *
 * **Arranca apagada, en el producto y acá.** Encuestar antes de tener volumen mide ruido, y una
 * encuesta que nadie pidió molesta a la persona a la que recién se le resolvió algo.
 */
const ESCALAS = [
  [3, 'tres opciones — la que más gente contesta'],
  [5, 'cinco — el punto medio habitual'],
  [10, 'diez — más resolución, menos respuestas'],
];

export default function LaConformidad({ config, handleSave, saving }) {
  const guardada = useMemo(() => config?.conformidad || {}, [config?.conformidad]);
  const [borrador, setBorrador] = useState(guardada);
  const [confirmar, setConfirmar] = useState({ open: false });

  useEffect(() => { setBorrador(config?.conformidad || {}); }, [config?.conformidad]);

  const sucio = useMemo(
    () => JSON.stringify(borrador) !== JSON.stringify(guardada),
    [borrador, guardada],
  );

  const cambiar = (campo, valor) => setBorrador((p) => ({ ...p, [campo]: valor }));

  const alternar = (prender) => {
    if (!prender) { cambiar('activa', false); return; }
    setConfirmar({
      open: true,
      title: 'Preguntarle a quien pidió',
      description:
        'A partir de que guardes, todo aviso cuyo texto incluya {{link_conformidad}} va a llevar ' +
        'un enlace real. Quien pidió el caso va a recibir esta pregunta, sin necesidad de tener ' +
        'cuenta. Leé la pregunta antes: es la que va a leer una persona.',
      confirmText: 'Prenderla',
      onConfirm: () => cambiar('activa', true),
    });
  };

  const activa = borrador.activa === true;

  return (
    <Card className={`rounded-sm ${activa ? 'border-brand-primary/40' : 'border-brand-border'}`}
          data-testid="la-conformidad">
      <ConfirmActionDialog
        {...confirmar}
        onOpenChange={(open) => setConfirmar((p) => ({ ...p, open }))}
      />
      <CardHeader className="py-2 px-3 border-b border-brand-border flex-row items-center gap-2 space-y-0">
        <Star className="w-3.5 h-3.5 text-brand-primary" />
        <CardTitle className="text-xs font-semibold flex-1">La conformidad del que pidió</CardTitle>
        <Badge variant={activa ? 'default' : 'secondary'} className="text-[9px] rounded-sm">
          {activa ? 'se pregunta' : 'apagada'}
        </Badge>
        <Switch
          data-testid="conformidad-activa"
          checked={activa}
          onCheckedChange={alternar}
        />
      </CardHeader>
      <CardContent className="p-3 space-y-3">
        <QueSeConfiguraAca
          testId="ayuda-conformidad"
          queEs="Una pregunta de un renglón a quien pidió el caso, para saber si le sirvió."
          paraQue="«En algún momento van a hacer una evaluación» fue el pedido. No existía nada: lo que hay en el sistema es QA interno sobre mensajes de agente, que escribe un supervisor y el usuario final no sabe que existe."
          aQueAfecta={<>La respuesta queda en el caso y entra al tablero como una medida más, <b>normalizada a 0–100</b> para que se puedan comparar áreas que preguntan sobre 5 con las que preguntan sobre 10. Viaja con el <i>n</i>: un 100% sobre una respuesta y uno sobre ochenta no valen lo mismo.</>}
          ejemplo={<>No se dispara sola: se pregunta <b>dentro de un aviso</b>, escribiendo <code className="bg-zinc-100 px-1 rounded-sm">{'{{link_conformidad}}'}</code> en su texto. Así podés preguntar al cerrar, o al entregar, o en las dos, sin tocar código.</>}
          ojo={<>El enlace <b>no pide cuenta</b> —quien pidió el caso puede no tenerla—, vale <b>una sola vez</b> y vence a los 30 días. Prenderla le manda una pregunta a gente real desde que guardás. Y si califica, <b>el caso se cierra</b>: pasa al estado que declara «exige conformidad».</>}
        />

        <div className="space-y-1">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-600">
            La pregunta
          </span>
          <Input
            data-testid="conformidad-pregunta"
            value={borrador.pregunta || ''}
            onChange={(e) => cambiar('pregunta', e.target.value)}
            placeholder="¿Te resolvimos lo que necesitabas?"
            className="rounded-sm text-xs h-7"
          />
          <p className="text-[10px] text-zinc-400">
            Una sola, y corta. Cada campo que se agrega se paga en respuestas que no llegan.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-600">
              La escala
            </span>
            <select
              data-testid="conformidad-escala"
              value={borrador.escala || 5}
              onChange={(e) => cambiar('escala', Number(e.target.value))}
              className="h-7 w-full text-[11px] border border-brand-border rounded-sm px-1 bg-white"
            >
              {ESCALAS.map(([n, texto]) => (
                <option key={n} value={n}>{texto}</option>
              ))}
            </select>
            <p className="text-[10px] text-zinc-400">
              Cambiarla no altera lo ya respondido: cada respuesta guarda con qué escala se dio.
            </p>
          </div>

          <div className="space-y-1">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-600">
              El agradecimiento
            </span>
            <Input
              data-testid="conformidad-agradecimiento"
              value={borrador.agradecimiento || ''}
              onChange={(e) => cambiar('agradecimiento', e.target.value)}
              placeholder="Gracias. Lo tenemos en cuenta."
              className="rounded-sm text-xs h-7"
            />
            <p className="text-[10px] text-zinc-400">Lo último que lee, después de contestar.</p>
          </div>
        </div>

        {/* **Qué pasa si no contesta.** Sin esto el caso queda esperando para siempre una
            calificación que no va a llegar, y la satisfacción se calcula sobre los pocos que
            contestan sin que nadie pueda ver cuántos no.

            Las dos perillas sólo aparecen con la encuesta prendida, y el servidor además lo
            rechaza: esperar una respuesta de alguien a quien **no se le preguntó** es fabricar un
            dato. */}
        {activa && (
          <div className="space-y-2 border-t border-brand-border pt-3">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-600">
              Si no contesta
            </span>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <span className="text-[10px] text-zinc-500">Recordarle a los</span>
                <div className="flex items-center gap-1">
                  <Input
                    type="number"
                    min="0"
                    max="365"
                    data-testid="conformidad-recordatorio-dias"
                    value={borrador.recordatorio_dias ?? 0}
                    onChange={(e) => cambiar('recordatorio_dias', Number(e.target.value || 0))}
                    className="rounded-sm text-xs h-7 w-20"
                  />
                  <span className="text-[10px] text-zinc-500">días · 0 lo apaga</span>
                </div>
                <p className="text-[10px] text-zinc-400">
                  Se le vuelve a preguntar <b>una sola vez</b> por invitación, con un enlace nuevo
                  —el anterior no se puede recuperar: del lado del servidor sólo vive su hash—.
                </p>
              </div>

              <div className="space-y-1">
                <span className="text-[10px] text-zinc-500">Cerrar el caso a los</span>
                <div className="flex items-center gap-1">
                  <Input
                    type="number"
                    min="0"
                    max="365"
                    data-testid="conformidad-cierre-dias"
                    value={borrador.cierre_automatico_dias ?? 0}
                    onChange={(e) => cambiar('cierre_automatico_dias', Number(e.target.value || 0))}
                    className="rounded-sm text-xs h-7 w-20"
                  />
                  <span className="text-[10px] text-zinc-500">días · 0 lo apaga</span>
                </div>
                <p className="text-[10px] text-zinc-400">
                  Pasa al estado que declara <b>«exige conformidad»</b> (en la pestaña Trabajo) y
                  queda <b>cerrado sin nota</b>: ese caso <b>no entra</b> en el promedio de
                  satisfacción, y se cuenta aparte.
                </p>
              </div>
            </div>
            <p className="text-[10px] text-zinc-400">
              El recordatorio tiene que llegar <b>antes</b> del cierre, o sale cuando el caso ya se
              cerró. El reloj de los dos arranca <b>cuando se le preguntó</b>, no cuando se
              resolvió: a quien nunca recibió la pregunta no se le cierra el caso por no contestar.
            </p>
          </div>
        )}

        <Button
          data-testid="guardar-conformidad"
          onClick={() => handleSave('conformidad', borrador)}
          disabled={saving || !sucio}
          className="bg-brand-primary hover:bg-[#001F6D] text-white rounded-sm text-xs h-7"
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
          Guardar
        </Button>
      </CardContent>
    </Card>
  );
}
